import React, { useEffect, useRef, useMemo } from "react";
import { Field } from "@progress/kendo-react-form";
import { FormRegion } from "../../../../components/formregion";
import { FormDropDownListById } from "../../../../forms/form-components";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { getWidgetKeyFromType } from "../../../../components/widgets/utils";
import {
  setFormValue,
  setStepPages,
  setSteps,
  steps,
  stepsName,
  stepsWithMultipleDataset,
  stepsWithMultipleDatasetName,
} from "../../../../redux/slices/createschedulepageslice";
import { useSearchParams } from "react-router-dom";

const SelectReportStep = ({
  onChangeReport,
  onChangeFormValue,
  onChange,
  ...props
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const pageState = useSelector((state) => state.createSchedulePage);

  const [searchParams] = useSearchParams();
  const reportIdFromUrl = searchParams.get("rptid");
  const widgetIdFromUrl = searchParams.get("widgetid");

  const prevReportIdRef = useRef(null);

  const lstOfReports = pageState.formValues.Reports ?? [];

  const simplifiedWidgets = [
    { Id: 0, Title: t("schedule.select") },
    ...(pageState.formValues.Widgets ?? []).map((w) => ({
      Id: w.Id,
      Title: `${getWidgetKeyFromType(w.WidgetTypeId) ?? t("extras.widget")} - ${
        w.Settings?.Title?.trim() || t("schedule.notitle")
      }`,
    })),
  ];

  /* Steps logic */
  useEffect(() => {
    const hasMultipleDatasets =
      pageState.formValues.Datasets?.length > 1 &&
      pageState.formValues.widgetId <= 0;

    dispatch(
      hasMultipleDatasets
        ? setSteps(stepsWithMultipleDatasetName)
        : setSteps(stepsName)
    );
    dispatch(
      hasMultipleDatasets
        ? setStepPages(stepsWithMultipleDataset)
        : setStepPages(steps)
    );
    dispatch(
      setFormValue({
        id: "HasMultipleDatasets",
        value: hasMultipleDatasets,
      })
    );
  }, [pageState.formValues.Datasets?.length, pageState.formValues.widgetId]);

  /* URL sync */
  useEffect(() => {
    if (!reportIdFromUrl) return;

    const report = lstOfReports.find((r) => r.ReferenceKey === reportIdFromUrl);
    if (!report) return;

    dispatch(setFormValue({ id: "reportId", value: reportIdFromUrl }));
    dispatch(setFormValue({ id: "rptId", value: report.Id }));
    onChangeReport(report.Id, onChange);
  }, [reportIdFromUrl, widgetIdFromUrl, pageState.formValues.Reports]);

  useEffect(() => {
    if (!widgetIdFromUrl) return;

    const widget = simplifiedWidgets.find(
      (r) => r.Id === Number(widgetIdFromUrl)
    );
    if (!widget) return;

    onChangeFormValue("widgetId", Number(widgetIdFromUrl) , onChange)
  }, [
    reportIdFromUrl,
    widgetIdFromUrl,
    pageState.formValues.Reports,
    pageState.formValues.Widgets,
  ]);

  /* Auto-clear & auto-select widget */
  useEffect(() => {
    const reportId = pageState.formValues.reportId;
    const widgetId = pageState.formValues.widgetId;
    const widgets = pageState.formValues.Widgets ?? [];

    // report changed → clear widget
    if (prevReportIdRef.current && prevReportIdRef.current !== reportId) {
      dispatch(setFormValue({ id: "widgetId", value: 0 }));
    }

    // widgets loaded + no widget selected → auto select first
    if (reportId && widgetId === 0 && widgets.length > 0) {
      dispatch(setFormValue({ id: "widgetId", value: widgets[0].Id }));
    }

    prevReportIdRef.current = reportId;
  }, [
    pageState.formValues.reportId,
    pageState.formValues.widgetId,
    pageState.formValues.Widgets?.length,
  ]);

  return (
    <FormRegion>
      <div className="form-group form-group-1" style={{ marginBottom: 50 }}>
        <Field
          id="reportId"
          name="reportId"
          label={t("reporting.design.report")}
          component={FormDropDownListById}
          dataItemKey="ReferenceKey"
          textField="Title"
          data={lstOfReports}
          onChange={(e) => onChangeReport(e.value, onChange)}
        />

        <Field
          id="widgetId"
          name="widgetId"
          label={t("extras.widget")}
          component={FormDropDownListById}
          dataItemKey="Id"
          textField="Title"
          data={simplifiedWidgets}
          disabled={!pageState.formValues.reportId}
          onChange={(e) => onChangeFormValue("widgetId", e.value, onChange)}
        />
      </div>
    </FormRegion>
  );
};

export default SelectReportStep;
